import React, { useState, useEffect } from 'react';
import {
  IonItem,
  IonLabel,
  IonInput,
  IonDatetime,
  IonTextarea,
  IonButton,
  IonToast,
  IonGrid,
  IonRow,
  IonCol,
  IonSpinner,
  IonIcon,
  IonCard,
  IonCardContent,
  IonPopover,
  IonSearchbar,
  IonList,
  IonAvatar,
  IonSegment,
  IonSegmentButton,
  IonChip,
} from '@ionic/react';
import {
  calendarOutline,
  timeOutline,
  locationOutline,
  mapOutline,
  businessOutline,
  globeOutline,
  documentTextOutline,
  imageOutline,
  sparklesOutline,
  checkmarkCircle,
  alertCircle,
  arrowForward,
  arrowBack,
  peopleOutline,
  createOutline,
  closeOutline,
} from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { eventService } from '../services/api';
import { useEvent } from '../contexts/EventContext';
import { Event } from '../types';
import { premierLeagueService, Match, Team } from '../services/premierLeague';
import { footballOutline } from 'ionicons/icons';
import { format } from 'date-fns';
import { createMatchName, cleanTeamName } from '../utils/teamUtils';
import { getStadiumForTeam } from '../utils/stadiumMappings';

interface CreateEventFormProps {
  event?: Event;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateEventForm: React.FC<CreateEventFormProps> = ({
  event,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { refreshEvents } = useEvent();
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [currentStep, setCurrentStep] = useState(0);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [formData, setFormData] = useState({
    name: event?.name || '',
    date: event ? new Date(event.date) : new Date(),
    time: event?.time || '',
    venue: event?.venue || '',
    address: event?.address || '',
    city: event?.city || '',
    country: event?.country || '',
    description: event?.description || '',
    imageUrl: event?.imageUrl || '',
    status: event?.status || 'active',
    capacity: event?.capacity?.toString() || '',
  });

  // Premier League integration states
  const [inputMode, setInputMode] = useState<'manual' | 'premier'>('manual');
  const [matches, setMatches] = useState<Match[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<Match | null>(null);
  const [competitionFilter, setCompetitionFilter] = useState<
    'all' | 'premier' | 'european'
  >('all');

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Load Premier League teams when mode changes
  useEffect(() => {
    if (inputMode === 'premier' && teams.length === 0) {
      loadTeams();
    }
  }, [inputMode]);

  // Load matches when team is selected
  useEffect(() => {
    if (selectedTeam) {
      loadTeamMatches(selectedTeam.id);
    }
  }, [selectedTeam]);

  // Reload matches when competition filter changes
  useEffect(() => {
    if (inputMode === 'premier' && teams.length > 0) {
      loadTeams();
    }
  }, [competitionFilter]);

  const loadTeams = async () => {
    setLoadingMatches(true);
    try {
      const plTeams = await premierLeagueService.getTeams();
      setTeams(plTeams);

      // Load matches based on competition filter
      let upcomingMatches: Match[] = [];
      if (competitionFilter === 'all') {
        upcomingMatches = await premierLeagueService.getAllMajorMatches();
      } else if (competitionFilter === 'premier') {
        upcomingMatches = await premierLeagueService.getUpcomingMatches([
          '2021',
        ]); // Premier League only
      } else if (competitionFilter === 'european') {
        upcomingMatches = await premierLeagueService.getEuropeanMatches();
      }

      setMatches(upcomingMatches);
    } catch (error) {
      console.error('Error loading teams:', error);
      setToastMessage('Failed to load teams and matches');
      setShowToast(true);
    } finally {
      setLoadingMatches(false);
    }
  };

  const loadTeamMatches = async (teamId: number) => {
    setLoadingMatches(true);
    try {
      const teamMatches = await premierLeagueService.getTeamMatches(teamId);
      setMatches(teamMatches);
    } catch (error) {
      console.error('Error loading team matches:', error);
      setToastMessage('Failed to load team matches');
      setShowToast(true);
    } finally {
      setLoadingMatches(false);
    }
  };

  const handleMatchSelect = (match: Match) => {
    setSelectedMatch(match);
    const matchDate = new Date(match.utcDate);
    setFormData({
      ...formData,
      name: createMatchName(match.homeTeam.name, match.awayTeam.name),
      date: matchDate,
      time: matchDate.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }),
      venue: match.venue || getStadiumForTeam(match.homeTeam.name),
      city: 'England',
      country: 'United Kingdom',
      description: `${match.competition.name} - Matchday ${match.matchday}`,
      imageUrl: match.homeTeam.crest,
    });
  };

  const filteredMatches = searchTerm
    ? matches.filter((match) => {
        const searchLower = searchTerm.toLowerCase();
        return (
          match.homeTeam.name.toLowerCase().includes(searchLower) ||
          match.awayTeam.name.toLowerCase().includes(searchLower) ||
          match.homeTeam.shortName.toLowerCase().includes(searchLower) ||
          match.awayTeam.shortName.toLowerCase().includes(searchLower)
        );
      })
    : matches;

  const validateStep = (step: number): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (step === 0) {
      if (!formData.name) newErrors.name = t('events.nameRequired');
      if (!formData.date) newErrors.date = t('events.dateRequired');
    } else if (step === 1) {
      if (!formData.venue) newErrors.venue = t('events.venueRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateStep(currentStep)) {
      return;
    }

    setLoading(true);
    try {
      const eventData: any = {
        name: formData.name,
        date: formData.date,
        venue: formData.venue,
        status: formData.status,
      };

      if (formData.time) eventData.time = formData.time;
      if (formData.address) eventData.address = formData.address;
      if (formData.city) eventData.city = formData.city;
      if (formData.country) eventData.country = formData.country;
      if (formData.description) eventData.description = formData.description;
      if (formData.imageUrl) eventData.imageUrl = formData.imageUrl;
      if (formData.capacity) eventData.capacity = parseInt(formData.capacity);

      if (event) {
        // Update existing event
        await eventService.updateEvent(event._id, eventData);
        setToastMessage(t('events.updateEventSuccess'));
      } else {
        // Create new event
        await eventService.createEvent(eventData);
        setToastMessage(t('events.eventCreated'));
      }

      setShowToast(true);
      await refreshEvents();
      setTimeout(() => {
        onSuccess();
      }, 1000);
    } catch (error: any) {
      console.error('Error creating event:', error);
      const errorMessage = Array.isArray(error.response?.data?.message)
        ? error.response.data.message[0]
        : error.response?.data?.message || error.message || t('errors.generic');
      setToastMessage(errorMessage);
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePremierLeagueEvent = async () => {
    if (!selectedMatch) return;

    setLoading(true);
    try {
      const eventData: any = {
        name: formData.name,
        date: formData.date,
        venue: formData.venue,
        status: formData.status,
        time: formData.time,
        city: formData.city,
        country: formData.country,
        description: formData.description,
        imageUrl: formData.imageUrl,
      };

      await eventService.createEvent(eventData);
      setToastMessage(t('events.eventCreated'));
      setShowToast(true);
      await refreshEvents();
      setTimeout(() => {
        onSuccess();
      }, 1000);
    } catch (error: any) {
      console.error('Error creating event:', error);
      const errorMessage = Array.isArray(error.response?.data?.message)
        ? error.response.data.message[0]
        : error.response?.data?.message || error.message || t('errors.generic');
      setToastMessage(errorMessage);
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      title: t('events.basicInfo'),
      icon: sparklesOutline,
    },
    {
      title: t('events.location'),
      icon: locationOutline,
    },
    {
      title: t('events.details'),
      icon: documentTextOutline,
    },
  ];

  const renderBasicInfo = () => (
    <div className="h-full flex flex-col">
      <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4 flex items-center">
        <IonIcon
          icon={sparklesOutline}
          className="text-2xl mr-2 text-purple-500"
        />
        {t('events.letsCreateEvent')}
      </h3>

      {/* Input Mode Selector */}
      <div className="flex gap-2 mb-3">
        <button
          onClick={() => setInputMode('manual')}
          className={`flex-1 py-2 px-4 rounded-lg font-medium text-sm transition-all duration-200 ${
            inputMode === 'manual'
              ? 'bg-purple-500 text-white shadow-md'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200'
          }`}
        >
          <IonIcon icon={createOutline} className="mr-2" />
          Manual Entry
        </button>
        <button
          onClick={() => setInputMode('premier')}
          className={`flex-1 py-2 px-4 rounded-lg font-medium text-sm transition-all duration-200 flex items-center justify-center ${
            inputMode === 'premier'
              ? 'bg-purple-500 text-white shadow-md'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200'
          }`}
        >
          <IonIcon icon={footballOutline} className="mr-2" />
          Football Matches
        </button>
      </div>

      {/* Competition Filter - only show when in premier mode */}
      {inputMode === 'premier' && (
        <div className="flex gap-2 mb-3">
          <button
            onClick={() => setCompetitionFilter('all')}
            className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all duration-200 ${
              competitionFilter === 'all'
                ? 'bg-purple-500 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
            }`}
          >
            All Competitions
          </button>
          <button
            onClick={() => setCompetitionFilter('premier')}
            className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all duration-200 ${
              competitionFilter === 'premier'
                ? 'bg-purple-500 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
            }`}
          >
            Premier League
          </button>
          <button
            onClick={() => setCompetitionFilter('european')}
            className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all duration-200 ${
              competitionFilter === 'european'
                ? 'bg-purple-500 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
            }`}
          >
            European
          </button>
        </div>
      )}

      {inputMode === 'premier' ? (
        <div className="flex-1 flex flex-col min-h-0 gap-2 relative">
          {/* Show matches directly with team filter options */}

          {/* Selected Team Header */}
          {selectedTeam && (
            <div className="bg-gradient-to-r from-purple-100 to-purple-50 dark:from-purple-900/30 dark:to-purple-800/20 rounded-lg p-2.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      setSelectedTeam(null);
                      setSearchTerm('');
                    }}
                    className="p-1 hover:bg-white/50 dark:hover:bg-black/20 rounded transition-colors"
                  >
                    <IonIcon icon={arrowBack} className="text-lg" />
                  </button>
                  <img
                    src={selectedTeam.crest}
                    alt={selectedTeam.name}
                    className="w-7 h-7 object-contain"
                  />
                  <div>
                    <h4 className="font-semibold text-sm">
                      {selectedTeam.name}
                    </h4>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {filteredMatches.length} of {matches.length} matches
                    </p>
                  </div>
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search..."
                  className="px-3 py-1 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 w-32"
                />
              </div>
            </div>
          )}

          {/* Search bar for all matches */}
          {!selectedTeam && (
            <div className="px-2 pb-2">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search teams or matches..."
                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
              />
            </div>
          )}

          {/* Matches List */}
          {loadingMatches ? (
            <div className="flex-1 flex items-center justify-center">
              <IonSpinner />
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              <div className="grid gap-2 pr-1">
                {filteredMatches.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No matches found</p>
                  </div>
                ) : (
                  filteredMatches.map((match) => {
                    const isSelected = selectedMatch?.id === match.id;
                    const matchDate = new Date(match.utcDate);
                    const isPast = matchDate < new Date();

                    return (
                      <div
                        key={match.id}
                        onClick={() => handleMatchSelect(match)}
                        className={`p-2.5 rounded-lg border cursor-pointer transition-all duration-200 group ${
                          isSelected
                            ? 'border-purple-500 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 shadow-sm scale-[1.02]'
                            : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 hover:shadow-sm bg-white dark:bg-gray-900'
                        } ${isPast ? 'opacity-50' : ''}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center space-x-2 flex-1 min-w-0">
                            <img
                              src={match.homeTeam.crest}
                              alt={match.homeTeam.name}
                              className="w-6 h-6 object-contain flex-shrink-0"
                            />
                            <span className="text-xs font-medium truncate">
                              {match.homeTeam.shortName}
                            </span>
                            <span className="text-[10px] text-gray-400 flex-shrink-0">
                              VS
                            </span>
                            <span className="text-xs font-medium truncate">
                              {match.awayTeam.shortName}
                            </span>
                            <img
                              src={match.awayTeam.crest}
                              alt={match.awayTeam.name}
                              className="w-6 h-6 object-contain flex-shrink-0"
                            />
                          </div>
                          <div className="flex items-center space-x-2">
                            {/* Competition Badge */}
                            {match.competition && (
                              <div className="flex flex-col items-center">
                                {match.competition.emblem && (
                                  <img
                                    src={match.competition.emblem}
                                    alt={match.competition.name}
                                    className="w-5 h-5 object-contain"
                                    title={match.competition.name}
                                  />
                                )}
                                <span className="text-[9px] text-gray-500 truncate max-w-[60px]">
                                  {match.competition.name
                                    .replace('UEFA ', '')
                                    .replace('League', '')
                                    .trim()}
                                </span>
                              </div>
                            )}
                            <div className="text-right flex-shrink-0">
                              <p className="text-[11px] font-medium">
                                {format(matchDate, 'MMM dd')}
                              </p>
                              <p className="text-[10px] text-gray-500">
                                {format(matchDate, 'HH:mm')}
                              </p>
                            </div>
                            {isSelected && (
                              <IonIcon
                                icon={checkmarkCircle}
                                className="text-purple-500 text-lg"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Floating Selected Match Preview */}
          {selectedMatch && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg p-3 shadow-lg m-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <IonIcon icon={checkmarkCircle} className="text-2xl" />
                  <div>
                    <p className="text-sm font-semibold">{formData.name}</p>
                    <p className="text-xs opacity-90">
                      {format(formData.date, 'MMM dd, yyyy')} • {formData.venue}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedMatch(null)}
                  className="p-1 hover:bg-white/20 rounded transition-colors"
                >
                  <IonIcon icon={closeOutline} />
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          <div className="space-y-4">
            <div className="relative group">
              <IonItem className="rounded-xl border-2 border-gray-200 dark:border-gray-700 group-hover:border-purple-400 transition-colors duration-300 shadow-sm group-hover:shadow-md">
                <IonIcon
                  icon={calendarOutline}
                  className="text-xl mr-3 text-purple-500"
                />
                <IonLabel position="floating" className="font-semibold">
                  {t('events.eventName')} *
                </IonLabel>
                <IonInput
                  value={formData.name}
                  onIonChange={(e) =>
                    setFormData({ ...formData, name: e.detail.value! })
                  }
                  required
                  disabled={loading}
                  className="font-medium"
                />
              </IonItem>
              {errors.name && (
                <div className="flex items-center text-red-500 text-sm mt-2 ml-2 transition-all duration-200 ease-out">
                  <IonIcon icon={alertCircle} className="mr-1" />
                  {errors.name}
                </div>
              )}
            </div>

            <IonRow className="gap-4">
              <IonCol size="12" sizeMd="6">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center">
                    <IonIcon
                      icon={calendarOutline}
                      className="text-purple-500 mr-2"
                    />
                    {t('events.date')} *
                  </label>
                  <div
                    className="relative group cursor-pointer"
                    onClick={() => setShowDatePicker(true)}
                  >
                    <div className="bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 group-hover:border-purple-400 transition-all duration-300 shadow-sm group-hover:shadow-md p-4 flex justify-between items-center">
                      <span className="font-medium text-gray-800 dark:text-gray-200">
                        {formData.date.toLocaleDateString('en-US', {
                          weekday: 'short',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </span>
                      <IonIcon
                        icon={calendarOutline}
                        className="text-gray-400"
                      />
                    </div>
                  </div>
                  <IonPopover
                    isOpen={showDatePicker}
                    onDidDismiss={() => setShowDatePicker(false)}
                    showBackdrop={true}
                    className="date-popover"
                  >
                    <IonDatetime
                      value={formData.date?.toISOString()}
                      min={new Date().toISOString()}
                      onIonChange={(e) => {
                        setFormData({
                          ...formData,
                          date: new Date(e.detail.value as string),
                        });
                        setShowDatePicker(false);
                      }}
                      presentation="date"
                      className="rounded-xl"
                    />
                  </IonPopover>
                  {errors.date && (
                    <div className="flex items-center text-red-500 text-sm mt-1 ml-1 transition-all duration-200 ease-out">
                      <IonIcon icon={alertCircle} className="mr-1" />
                      {errors.date}
                    </div>
                  )}
                </div>
              </IonCol>
              <IonCol size="12" sizeMd="6">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center">
                    <IonIcon
                      icon={timeOutline}
                      className="text-purple-500 mr-2"
                    />
                    {t('events.time')}
                  </label>
                  <div className="relative group">
                    <input
                      type="time"
                      value={formData.time}
                      onChange={(e) =>
                        setFormData({ ...formData, time: e.target.value })
                      }
                      disabled={loading}
                      className="w-full bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 group-hover:border-purple-400 transition-all duration-300 shadow-sm group-hover:shadow-md p-4 font-medium text-gray-800 dark:text-gray-200 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200 dark:focus:ring-purple-800 [color-scheme:light] dark:[color-scheme:dark]"
                    />
                  </div>
                </div>
              </IonCol>
            </IonRow>
          </div>
        </div>
      )}
    </div>
  );

  const renderLocation = () => (
    <div className="h-full overflow-y-auto">
      <div className="space-y-4">
        <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 flex items-center">
          <IonIcon
            icon={locationOutline}
            className="text-2xl mr-2 text-blue-500"
          />
          {t('events.whereIsIt')}
        </h3>

        <div className="space-y-3">
          <div className="relative group">
            <IonItem className="rounded-lg border border-gray-200 dark:border-gray-700 group-hover:border-blue-400 transition-colors">
              <IonIcon
                icon={businessOutline}
                className="text-lg mr-2 text-blue-500"
              />
              <IonLabel position="floating" className="font-medium text-sm">
                {t('events.venue')} *
              </IonLabel>
              <IonInput
                value={formData.venue}
                onIonChange={(e) =>
                  setFormData({ ...formData, venue: e.detail.value! })
                }
                required
                disabled={loading}
              />
            </IonItem>
            {errors.venue && (
              <p className="text-red-500 text-xs mt-1 ml-2">{errors.venue}</p>
            )}
          </div>

          <IonRow>
            <IonCol size="12" sizeMd="6">
              <div className="relative group">
                <IonItem className="rounded-xl border-2 border-gray-200 dark:border-gray-700 group-hover:border-blue-400 transition-colors duration-300 shadow-sm group-hover:shadow-md">
                  <IonIcon
                    icon={mapOutline}
                    className="text-xl mr-3 text-blue-500"
                  />
                  <IonLabel position="floating" className="font-semibold">
                    {t('events.city')}
                  </IonLabel>
                  <IonInput
                    value={formData.city}
                    onIonChange={(e) =>
                      setFormData({ ...formData, city: e.detail.value! })
                    }
                    disabled={loading}
                    className="font-medium"
                  />
                </IonItem>
              </div>
            </IonCol>
            <IonCol size="12" sizeMd="6">
              <div className="relative group">
                <IonItem className="rounded-xl border-2 border-gray-200 dark:border-gray-700 group-hover:border-blue-400 transition-colors duration-300 shadow-sm group-hover:shadow-md">
                  <IonIcon
                    icon={globeOutline}
                    className="text-xl mr-3 text-blue-500"
                  />
                  <IonLabel position="floating" className="font-semibold">
                    {t('events.country')}
                  </IonLabel>
                  <IonInput
                    value={formData.country}
                    onIonChange={(e) =>
                      setFormData({ ...formData, country: e.detail.value! })
                    }
                    disabled={loading}
                    className="font-medium"
                  />
                </IonItem>
              </div>
            </IonCol>
          </IonRow>

          <div className="relative group">
            <IonItem className="rounded-xl border-2 border-gray-200 dark:border-gray-700 group-hover:border-blue-400 transition-colors duration-300 shadow-sm group-hover:shadow-md">
              <IonIcon
                icon={locationOutline}
                className="text-xl mr-3 text-blue-500"
              />
              <IonLabel position="floating" className="font-semibold">
                {t('events.address')}
              </IonLabel>
              <IonInput
                value={formData.address}
                onIonChange={(e) =>
                  setFormData({ ...formData, address: e.detail.value! })
                }
                disabled={loading}
                className="font-medium"
              />
            </IonItem>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDetails = () => (
    <div className="h-full overflow-y-auto">
      <div className="space-y-4">
        <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 flex items-center">
          <IonIcon
            icon={documentTextOutline}
            className="text-2xl mr-2 text-green-500"
          />
          {t('events.tellUsMore')}
        </h3>

        <div className="space-y-3">
          <div className="relative group">
            <IonItem className="rounded-lg border border-gray-200 dark:border-gray-700 group-hover:border-green-400 transition-colors h-24">
              <IonIcon
                icon={documentTextOutline}
                className="text-xl mr-3 text-green-500 self-start mt-4"
              />
              <IonLabel position="floating" className="font-semibold">
                {t('events.description')}
              </IonLabel>
              <IonTextarea
                value={formData.description}
                onIonChange={(e) =>
                  setFormData({ ...formData, description: e.detail.value! })
                }
                rows={4}
                disabled={loading}
                className="font-medium"
              />
            </IonItem>
          </div>

          <div className="relative group">
            <IonItem className="rounded-xl border-2 border-gray-200 dark:border-gray-700 group-hover:border-green-400 transition-colors duration-300 shadow-sm group-hover:shadow-md">
              <IonIcon
                icon={imageOutline}
                className="text-xl mr-3 text-green-500"
              />
              <IonLabel position="floating" className="font-semibold">
                {t('events.imageUrl')}
              </IonLabel>
              <IonInput
                value={formData.imageUrl}
                onIonChange={(e) =>
                  setFormData({ ...formData, imageUrl: e.detail.value! })
                }
                disabled={loading}
                placeholder="https://example.com/image.jpg"
                className="font-medium"
              />
            </IonItem>
          </div>

          <div className="relative group">
            <IonItem className="rounded-xl border-2 border-gray-200 dark:border-gray-700 group-hover:border-green-400 transition-colors duration-300 shadow-sm group-hover:shadow-md">
              <IonIcon
                icon={peopleOutline}
                className="text-xl mr-3 text-green-500"
              />
              <IonLabel position="floating" className="font-semibold">
                {t('events.capacity')}
              </IonLabel>
              <IonInput
                type="number"
                value={formData.capacity}
                onIonChange={(e) =>
                  setFormData({ ...formData, capacity: e.detail.value! })
                }
                disabled={loading}
                className="font-medium"
                placeholder="1000"
              />
            </IonItem>
          </div>

          {formData.imageUrl && (
            <div className="mt-4">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2 flex items-center">
                <IonIcon icon={imageOutline} className="mr-1 text-green-500" />
                Image Preview
              </p>
              <div className="relative group rounded-xl overflow-hidden border-2 border-gray-200 dark:border-gray-700 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 shadow-inner">
                <div className="aspect-video w-full flex items-center justify-center p-4">
                  <img
                    src={formData.imageUrl}
                    alt="Event preview"
                    className="max-w-full max-h-full object-contain rounded-lg shadow-md"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src =
                        'https://via.placeholder.com/400x200?text=Invalid+Image+URL';
                    }}
                  />
                </div>
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-5 transition-all duration-200 pointer-events-none" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return renderBasicInfo();
      case 1:
        return renderLocation();
      case 2:
        return renderDetails();
      default:
        return renderBasicInfo();
    }
  };

  return (
    <>
      <div className="h-full flex flex-col">
        {/* Compact Step Indicator */}
        <div className="px-6 pt-4 pb-2">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div
                key={index}
                className={`flex items-center ${
                  index < steps.length - 1 ? 'flex-1' : ''
                }`}
              >
                <div
                  className={`flex items-center cursor-pointer ${
                    index <= currentStep ? 'opacity-100' : 'opacity-40'
                  }`}
                  onClick={() => index < currentStep && setCurrentStep(index)}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                      index < currentStep
                        ? 'bg-green-500 text-white'
                        : index === currentStep
                        ? 'bg-purple-500 text-white'
                        : 'bg-gray-300 dark:bg-gray-600 text-gray-500'
                    }`}
                  >
                    <IonIcon
                      icon={index < currentStep ? checkmarkCircle : step.icon}
                      className="text-lg"
                    />
                  </div>
                  <span
                    className={`ml-2 text-sm font-medium hidden md:block ${
                      index <= currentStep
                        ? 'text-gray-800 dark:text-gray-100'
                        : 'text-gray-400'
                    }`}
                  >
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className="flex-1 mx-2">
                    <div
                      className={`h-0.5 transition-all duration-500 ${
                        index < currentStep
                          ? 'bg-green-500'
                          : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {renderCurrentStep()}
          </div>

          {/* Quick Summary Bar */}
          {currentStep > 0 && formData.name && (
            <div className="px-6 py-2 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-4 text-xs">
                <span className="font-medium">{formData.name}</span>
                {formData.venue && (
                  <>
                    <span className="text-gray-400">•</span>
                    <span>{formData.venue}</span>
                  </>
                )}
                {formData.date && (
                  <>
                    <span className="text-gray-400">•</span>
                    <span>{format(formData.date, 'MMM dd')}</span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between items-center px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
            <IonButton
              fill="clear"
              onClick={currentStep === 0 ? onClose : prevStep}
              disabled={loading}
            >
              <IonIcon icon={arrowBack} slot="start" />
              {currentStep === 0 ? t('common.cancel') : t('common.back')}
            </IonButton>

            {/* For Premier League mode with selected match, show Create button immediately */}
            {inputMode === 'premier' && selectedMatch && currentStep === 0 ? (
              <IonButton
                fill="solid"
                onClick={handleCreatePremierLeagueEvent}
                disabled={loading}
                color="success"
              >
                {loading ? (
                  <IonSpinner name="crescent" />
                ) : (
                  <>
                    {t('common.create')}
                    <IonIcon icon={checkmarkCircle} slot="end" />
                  </>
                )}
              </IonButton>
            ) : currentStep < steps.length - 1 ? (
              <IonButton
                fill="solid"
                onClick={nextStep}
                disabled={loading}
                color="primary"
              >
                {t('common.next')}
                <IonIcon icon={arrowForward} slot="end" />
              </IonButton>
            ) : (
              <IonButton
                fill="solid"
                type="submit"
                disabled={loading}
                color="success"
              >
                {loading ? (
                  <IonSpinner name="crescent" />
                ) : (
                  <>
                    {event ? t('common.update') : t('common.create')}
                    <IonIcon icon={checkmarkCircle} slot="end" />
                  </>
                )}
              </IonButton>
            )}
          </div>
        </form>
      </div>

      <IonToast
        isOpen={showToast}
        onDidDismiss={() => setShowToast(false)}
        message={toastMessage}
        duration={3000}
        position="bottom"
        cssClass="font-semibold"
      />
    </>
  );
};

export default CreateEventForm;
